import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  BasicInteractions360,
  BasicInteractions3D,
  filterInteractionDefinitions,
  InteractionDefintion,
  isSameInteractionDefinition,
} from "../../features/interaction_picker/InteractionDefinitions";
import { RootState } from "../Store";
import { TrainingType } from "./TrainingSlice";

interface InteractionPickerState {
  interactionDefinitions: InteractionDefintion[];
  mostRecent: InteractionDefintion[];
  selectedInteraction: InteractionDefintion;
  filter: string;
  selectedPropStateId: string;
}

const initialState: InteractionPickerState = {
  interactionDefinitions: BasicInteractions360,
  mostRecent: [],
  selectedInteraction: BasicInteractions360[0],
  filter: "",
  selectedPropStateId: "",
};

export const interactionPickerSlice = createSlice({
  name: "interactionPicker",
  initialState: initialState,
  reducers: {
    setInteractionPickerContext: (
      state,
      action: PayloadAction<TrainingType>
    ) => {
      if (action.payload === TrainingType.Training3D) {
        state.interactionDefinitions = BasicInteractions3D;
        state.mostRecent = [];
        state.filter = "";
        state.selectedInteraction = BasicInteractions3D[0];
      } else if (action.payload === TrainingType.Training360) {
        state.interactionDefinitions = BasicInteractions360;
        state.mostRecent = [];
        state.filter = "";
        state.selectedInteraction = BasicInteractions360[0];
      }
    },
    setSelectedInteractionDefinition: (
      state,
      action: PayloadAction<InteractionDefintion>
    ) => {
      state.selectedInteraction = action.payload;
    },
    addMostRecentInteraction: (
      state,
      action: PayloadAction<InteractionDefintion>
    ) => {
      const existingIndex = state.mostRecent.findIndex((mostRecent) =>
        isSameInteractionDefinition(mostRecent, action.payload)
      );
      if (existingIndex !== -1) state.mostRecent.splice(existingIndex, 1);

      // A plain copy — selection is compared by identity (type plus operator),
      // so this no longer needs a mangled name to tell it apart from the
      // definition it was copied from.
      const copy = { ...action.payload };
      state.mostRecent.unshift(copy);

      if (state.mostRecent.length > 4) state.mostRecent.pop();

      state.selectedInteraction = copy;
    },
    setInteractionPickerFilter: (state, action: PayloadAction<string>) => {
      state.filter = action.payload;
    },
    setSelectedInteractionPropStateId: (
      state,
      action: PayloadAction<string>
    ) => {
      state.selectedPropStateId = action.payload;
    },
  },
});

export const {
  setInteractionPickerContext,
  setSelectedInteractionDefinition,
  addMostRecentInteraction,
  setInteractionPickerFilter,
  setSelectedInteractionPropStateId,
} = interactionPickerSlice.actions;

export const selectInteractionDefinitions = (state: RootState) =>
  state.interactionPicker.interactionDefinitions;

export const selectFilter = (state: RootState) =>
  state.interactionPicker.filter;

/** Unfiltered — callers that do their own matching want the whole list. */
export const selectMostRecentInteractions = (state: RootState) =>
  state.interactionPicker.mostRecent;

// Memoised because both selectors build a new array, which would otherwise
// re-render every subscriber on any unrelated state change.
export const selectFilteredInteractionDefinitions = createSelector(
  [selectInteractionDefinitions, selectFilter],
  filterInteractionDefinitions
);

export const selectSelectedInteractionDefinition = (state: RootState) =>
  state.interactionPicker.selectedInteraction;

export const selectMostRecentInteractionDefinitions = createSelector(
  [selectMostRecentInteractions, selectFilter],
  filterInteractionDefinitions
);

export const selectSelectedInteractionPropStateId = (state: RootState) =>
  state.interactionPicker.selectedPropStateId;

export default interactionPickerSlice.reducer;
